import React, { createContext, useContext, useState, useEffect } from 'react';
import log from 'loglevel';
import { ApiTransport, setupApiLogging } from '../services/logging';
import { useConfig } from './useConfig';

export interface LoggingContextProps {
  log: log.Logger;
  apiTransport?: ApiTransport;
}

export const LoggingContext = createContext<LoggingContextProps | null>(null);

export const LoggingProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [loggingProps, setLoggingProps] = useState<LoggingContextProps | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const { logging } = useConfig();

  useEffect(() => {
    log.setLevel((logging.level as log.LogLevelDesc) || 'info');

    let apiTransport: ApiTransport | undefined;
    // Save the original methodFactory
    const originalFactory = log.methodFactory;

    if (logging.includeApi) {
      apiTransport = new ApiTransport({
        baseURL: logging.baseURL || 'https://default-api-url.com',
        timeout: logging.timeout || 5000,
      });
      setupApiLogging(apiTransport);
    }

    setLoggingProps({ log, apiTransport });
    setLoading(false);

    // Cleanup: restore the original methodFactory
    return () => {
      log.methodFactory = originalFactory;
      log.setLevel(log.getLevel());
    };
  }, [logging]);

  if (loading) {
    return null;
  }

  return (
    <LoggingContext.Provider value={loggingProps}>
      {children}
    </LoggingContext.Provider>
  );
};

export function useLogging(): LoggingContextProps {
  const context = useContext(LoggingContext);
  if (!context) throw Error('Cannot use useLogging outside of LoggingProvider');
  return context;
}
